import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  SimpleGrid,
  VStack,
  HStack,
  Divider,
  Box,
  Text,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import theme from "../../../../../global/theme";
import { Field, inputProps } from "./modalStyles";
import CheckboxGroupField from "./CheckboxGroupField";

const GenericFormModal = ({
  title,
  fields,
  initialValues = {},
  isLoading,
  error,
  onSubmit,
  onClose,
}) => {
  
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  // 1. add watch to the destructure
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({ defaultValues: initialValues });

  // 2. live form values (re-renders on every change, which is what we want)
  const values = watch();

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      size="4xl"
      dir={isRTL ? "rtl" : "ltr"}
    >
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit((values) => onSubmit(values))}>
          <ModalHeader>{title}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack spacing={6} align="stretch">
              {error && (
                <Alert status="error" variant="left-accent">
                  <AlertIcon />
                  <Text>{error}</Text>
                </Alert>
              )}

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {fields.map((f) => (
                  <Box
                    key={f.key}
                    gridColumn={
                      f.type === "checkboxes" ? { md: "span 2" } : undefined
                    }
                  >
                    <Field label={f.label} error={errors[f.key]?.message}>
                      {f.type === "checkboxes" ? (
                        <Controller
                          name={f.key}
                          control={control}
                          rules={
                            f.required
                              ? {
                                  validate: (v) =>
                                    (v && v.length > 0) ||
                                    t("validation.required"),
                                }
                              : undefined
                          }
                          render={({ field }) => (
                            <CheckboxGroupField
                              options={f.options}
                              value={field.value || []}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      ) : (
                        <Input
                          type="text"
                          {...inputProps}
                          placeholder={f.label}
                          {...register(
                            f.key,
                            f.required
                              ? { required: t("validation.required") }
                              : {},
                          )}
                        />
                      )}
                    </Field>
                     {f.renderAfter && f.renderAfter(values)}
                  </Box>
                ))}
              </SimpleGrid>

              <Divider />

              <HStack justifyContent="flex-end" spacing={3}>
                <Button
                  type="button"
                  colorScheme="gray"
                  textTransform="capitalize"
                  onClick={onClose}
                >
                  {t("general.close")}
                </Button>
                <Button
                  type="submit"
                  bg={theme.navy}
                  color="white"
                  textTransform="capitalize"
                  isLoading={isLoading}
                  _hover={{ opacity: 0.9 }}
                >
                  {t("general.save")}
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default GenericFormModal;
